import 'scroll-shadow-element';
import { ApolloError } from '@apollo/client';
import { add } from 'date-fns';
import { RefObject, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import tw, { css, styled } from 'twin.macro';
import 'styled-components/macro';

import { AssignmentMarkdownProps } from 'component-lib/molecules/assignment-markdown/AssignmentMarkdown';
import { useNotificationContext } from 'core/context/Notifications';
import { useFeatureFlag } from 'core/molecules/feature-flag-manager/FeatureFlagManager';
import { CollapseButton } from 'play/atoms/collapse-button/CollapseButton';
import { PlacementPicker } from 'play/atoms/placement-picker/PlacementPicker';
import CompletionBanner from 'play/legacy/assignment/CompletionBanner';
import { ReactComponent as NotesIcon } from 'play/legacy/assignment/images/notes.svg';
import { ChallengeTimer } from 'play/legacy/assignment/timer/ChallengeTimer';
import { ChallengeActions } from 'play/molecules/challenge-actions/ChallengeActions';
import { Progress, ProgressProps } from 'play/organisms/progress/Progress';
import { AssignmentPosition } from 'play/support/assignmentPosition';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { ChallengeStatus, LearnEnvironmentQuery, Mode, useExtendSandboxTtlMutation } from 'types/graphql';

import { ExtendTTLModal } from '../extend-ttl-modal/ExtendTTLModal';
import { AssignmentContent } from './AssignmentContent';

export const Top = tw.div`h-[50px] min-h-[50px] w-full bg-[#313d53] flex flex-col box-border`;

export const TopContent = styled.div(({ position = 'right' }: { position?: AssignmentPosition }) => [
  tw`text-white flex justify-between items-center h-[48px] px-2 gap-2`,
  position === 'right' ? tw`flex-row` : tw`flex-row-reverse`,
]);

export const TopContentActions = styled.div(({ position = 'right' }: { position?: AssignmentPosition }) => [
  tw`flex gap-2`,
  position === 'right' ? tw`flex-row` : tw`flex-row-reverse`,
]);

export const Bottom = styled.div`
  flex-shrink: 0;
  background-color: #313d53;
`;

const IconContainer = styled.button(() => [
  tw`flex items-center cursor-pointer outline-none select-none text-white`,
  css`
    -webkit-tap-highlight-color: transparent;

    svg {
      width: 15px;
    }
  `,
]);

export interface AssignmentProps {
  mode: Mode;
  teamSlug: string;
  track: NonNullable<LearnEnvironmentQuery['track']>;
  challenges: ProgressProps['challenges'];
  challenge: NonNullable<LearnEnvironmentQuery['challenge']>;
  isChecking: boolean;
  completed: boolean;
  collapsed: boolean;
  toggleCollapse(): void;
  hasNextChallenge?: boolean;
  stopTrackCalled: boolean;
  skippingEnabled: boolean;
  toggleNotes(): void;
  onCheckChallenge(): void;
  onNext(): void;
  onExpire(): void;
  onSkip(): void;
  trackReviewsEnabled?: boolean;
  sidebarPosition?: AssignmentPosition;
  onSidebarPositionChange?: (newValue: AssignmentPosition) => void;
}

export function Assignment({
  mode,
  teamSlug,
  track,
  challenges,
  challenge,
  onCheckChallenge,
  isChecking,
  toggleNotes,
  collapsed,
  toggleCollapse,
  completed,
  hasNextChallenge,
  onNext,
  onExpire,
  stopTrackCalled,
  skippingEnabled,
  onSkip,
  trackReviewsEnabled,
  sidebarPosition = 'right',
  onSidebarPositionChange,
}: AssignmentProps) {
  const { dispatch } = useNotificationContext();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const history = useHistory();
  const { inviteID } = useParams<{ inviteID?: string }>();
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [isExtendTTLFeatureActive] = useFeatureFlag('feature_extend_TTL');
  const [extendSandboxTtlMutation] = useExtendSandboxTtlMutation();
  const onExtend = async () => {
    if (!track.ttl || !track.lab_config?.extendTTL || !track.participant?.id) {
      return;
    }

    const newTtl = add(new Date(track.ttl), { seconds: track.lab_config.extendTTL }).toISOString();

    try {
      await extendSandboxTtlMutation({ variables: { sandboxID: track.participant?.id, until: newTtl } });

      dispatch('success', '', 'Sandbox instance TTL extended');
      setShowExtendModal(false);
    } catch (error) {
      const message =
        (error as ApolloError).message ?? 'Something went wrong while extending sandbox instance TTL, please try again';

      dispatch('error', 'Failed to extend sandbox instance TTL', message, 10);
    }
  };

  const scrollLastPanelIntoView = useCallback((lastPanelRef: RefObject<HTMLDivElement>) => {
    // timeout hack due to panel's height animation
    window.setTimeout(() => {
      const scrollableParentElement = getScrollableParent(lastPanelRef.current?.parentElement ?? null);

      if (!lastPanelRef.current || !scrollableParentElement) {
        return;
      }

      const topOffsetPx = 50;
      const cardTopPositionPx = lastPanelRef.current.offsetTop + topOffsetPx;
      const scrollableElementBottomPosition = scrollableParentElement.scrollTop + scrollableParentElement.clientHeight;

      if (cardTopPositionPx > scrollableElementBottomPosition) {
        const scrollOffsetPx = 300;

        // `scrollIntoView` doesn't provide offset option and scrolls the whole element into the view
        // leaving all content above out of the viewport
        scrollableParentElement.scroll({
          top: scrollableParentElement.scrollTop + scrollOffsetPx,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, []);

  const onAccordionToggle: AssignmentMarkdownProps['onToggle'] = (isOpen, sections, section, lastPanelRef) => {
    const index = sections.indexOf(section);

    if (isOpen && index === sections.length - 1) {
      lastPanelRef && scrollLastPanelIntoView(lastPanelRef);
    }

    const accordionNumber = index + 1;
    const accordionCount = sections.length;
    const accordionNumberPct = accordionNumber / accordionCount;

    trackAmplitudeEvent({
      name: 'Lab - Challenge - Toggled accordion',
      data: {
        action: isOpen ? 'expand' : 'collapse',
        accordionNumber,
        accordionCount,
        accordionNumberPct,
      },
    });
  };

  const onClickNotesIcon = () => {
    toggleNotes();
    trackAmplitudeEvent({ name: 'Lab - Challenge - Opened notes' });
  };

  const onClickCompletionBanner = () => {
    trackAmplitudeEvent({ name: 'Lab - Challenge - Clicked correct panel' });
    onNext();
  };

  const onClickCollapseSidebar = () => {
    trackAmplitudeEvent({ name: 'Lab - Challenge - Toggled sidebar', data: { action: 'closed' } });
    toggleCollapse();
  };
  const closeToPath = inviteID ? `/${teamSlug}/invite/${inviteID}` : track?.permalink;

  const challengeCompleted = challenge.status === ChallengeStatus.Completed;
  const canSkip = skippingEnabled && !challengeCompleted;

  return (
    <>
      <Top>
        <TopContent position={sidebarPosition}>
          <TopContentActions position={sidebarPosition}>
            <IconContainer onClick={onClickCollapseSidebar}>
              <CollapseButton direction={sidebarPosition} collapsed={collapsed} />
            </IconContainer>
            {!collapsed && (
              <PlacementPicker
                onChange={newValue => {
                  onSidebarPositionChange?.(newValue);
                }}
                value={sidebarPosition}
              />
            )}
            {!collapsed && !!challenge?.notes?.length && (
              <IconContainer onClick={onClickNotesIcon} aria-label="notes">
                <NotesIcon tw="w-2" />
              </IconContainer>
            )}
          </TopContentActions>

          {!collapsed && (
            <div tw="flex-1 flex flex-row justify-end gap-2">
              <Progress
                teamSlug={teamSlug}
                challenges={challenges}
                currentChallenge={challenge}
                mode={mode}
                track={track}
                skippingEnabled={skippingEnabled}
                tw="flex-1"
                trackReviewsEnabled={trackReviewsEnabled}
                challengeTimer={
                  <ChallengeTimer
                    tw="ml-1"
                    track={track}
                    onExpire={onExpire}
                    showExtendModal={() => setShowExtendModal(true)}
                    canExtend={track.participant?.ttlExtendedByUser === false}
                  />
                }
              />
            </div>
          )}
        </TopContent>
      </Top>
      {!collapsed && (
        <>
          {!stopTrackCalled ? (
            <AssignmentContent onAccordionToggle={onAccordionToggle} challenge={challenge} />
          ) : (
            <div tw="grow" />
          )}
          <Bottom>
            {completed ? (
              <CompletionBanner
                subtitle={
                  !hasNextChallenge ? 'You have successfully completed the track!' : 'Loading your next challenge...'
                }
                onComplete={onClickCompletionBanner}
              />
            ) : (
              !stopTrackCalled && (
                <ChallengeActions
                  isCompleted={challengeCompleted}
                  canCheck={!!challenge.can_check}
                  canExit={mode === Mode.Platform}
                  canSkip={canSkip}
                  exitTo={{ pathname: closeToPath || undefined, state: history.location.state }}
                  isChecking={isChecking}
                  onCheck={onCheckChallenge}
                  onNext={onNext}
                  onSkip={onSkip}
                />
              )
            )}
          </Bottom>
        </>
      )}
      {isExtendTTLFeatureActive && showExtendModal && track.lab_config?.extendTTL && (
        <ExtendTTLModal
          extraMinutes={Math.floor(track.lab_config?.extendTTL / 60)}
          onExpire={() => {
            onExpire();
            setShowExtendModal(false);
          }}
          onExtend={onExtend}
        />
      )}
    </>
  );
}

function getScrollableParent(node: HTMLElement | null): HTMLElement | null {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  }

  return getScrollableParent(node.parentNode as HTMLElement | null);
}
